import React, { useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Card, CardContent, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { template } from 'lodash'
import { CardTitle } from 'components/card/NewCard'
import SimpleInput, { INPUT_TYPES } from 'components/forms/SimpleInput'
import { INDEXED_MODULES, MODULE_PARAMETER, OBSERVATORY, WIDGETS } from './ObservatoryConstants'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import ToastrAction from 'toastr/actions/ToastrAction'
import moment from 'moment'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import useDebounce from 'utils/customHook/useDebounce'
import { ModuleHabsContext, ModuleHabsDispatchContext } from './AdminModulesApp'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'

const MAX_LOGIN_LENGTH = 50
const getLimitedLoginLength = (conventionalName = '') => MAX_LOGIN_LENGTH - conventionalName.length - 1

const ModuleUrl = ({
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},
    foundModuleParameter,
    moduleLogin = '',
}) => {
    const {
        applicativeUsersParameters,
    } = useSelector(store => ({
        applicativeUsersParameters: store.UserReducer.applicativeUsersParameters,
    }), shallowEqual)

    const moduleParamsDispatch = useContext(ModuleHabsDispatchContext)

    const [moduleUrl, setModuleUrl] = useState(foundModuleParameter)

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== MODULE_PARAMETER),
            getGlobalModuleParameter(MODULE_PARAMETER, moduleUrl),
        ])
    }, 200, [moduleUrl])

    const moduleUrlError = useMemo(() => applicativeUsersParameters.some(s => s.login !== moduleLogin && s.parameter === MODULE_PARAMETER && s.value === moduleUrl), [applicativeUsersParameters, moduleLogin, moduleUrl])

    useDebounce(() => {
        moduleParamsDispatch({ type: 'moduleUrlError', payload: moduleUrlError })
    }, 200, [moduleUrlError])

    return (
        <Stack>
            <SimpleInput
                title={i18n.url}
                value={moduleUrl}
                onChange={setModuleUrl}
                type={INPUT_TYPES.domain}
                placeholder='observatoire.fr'
                error={moduleUrlError}
                required
            />
            {!!moduleUrlError && (
                <Typography variant='formError'>{i18n.moduleUrlError}</Typography>
            )}
        </Stack>
    )
}

ModuleUrl.propTypes = {
    setModuleParameters: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    foundModuleParameter: PropTypes.string,
    moduleLogin: PropTypes.string,
}

const DefinitionPanel = ({
    module = {},
    setModule = () => {},
}) => {
    const {
        applicativeUserSettings,
    } = useSelector(store => ({
        applicativeUserSettings: store.AdministrationReducer.applicativeUserSettings,
    }), shallowEqual)

    const dispatch = useDispatch()

    const moduleParams = useContext(ModuleHabsContext)
    const moduleParamsDispatch = useContext(ModuleHabsDispatchContext)

    const [moduleParameters, setModuleParameters] = useState(moduleParams?.moduleParameters?.length ? moduleParams?.moduleParameters : applicativeUserSettings.filter(aus => aus.login === module.login))

    useDebounce(() => {
        moduleParamsDispatch({ type: 'moduleParameters', payload: moduleParameters })
    }, 100, [moduleParameters])

    const getGlobalModuleParameter = (parameter, value) => {
        const foundParam = moduleParameters.find(p => p.parameter === parameter)
        return {
            ...{
                login: module.login,
                parameter,
                value: value || undefined,
                updateDate: moment().valueOf(),
            },
            isNew: !foundParam?.value,
        }
    }

    const foundModuleParameter = moduleParameters.find(p => p.parameter === MODULE_PARAMETER)?.value

    return (
        <Stack spacing={1} sx={{ width: '100%' }}>
            <Typography variant='sectionTitle'>{template(i18n.adminModuleDefinitionSubtitle)({ module: module.module })}</Typography>
            <Card variant='newCard'>
                <CardTitle title={<Typography variant='cardTitle'>{i18n.moduleDescription}</Typography>} />
                <CardContent>
                    <Stack spacing={1}>
                        <Grid container columnSpacing={1}>
                            <Grid size='grow'>
                                <SimpleInput
                                    title={i18n.name}
                                    value={module.name}
                                    onChange={name => setModule(prev => ({ ...prev, name }))}
                                    maxLength={getLimitedLoginLength(module.conventionalName)}
                                    required
                                />
                            </Grid>
                            {module.conventionalName !== OBSERVATORY.conventionalName && (
                                <Grid size={3}>
                                    <SuperMultiAutocomplete
                                        label={i18n.widgetType}
                                        options={WIDGETS}
                                        values={module.conventionalName}
                                        onChange={conventionalName => setModule(prev => {
                                            const foundModule = INDEXED_MODULES[conventionalName]
                                            return {
                                                ...prev,
                                                ...foundModule,
                                                module: foundModule.name,
                                                name: prev.name || `${foundModule.name} ${moment().valueOf()}`,
                                            }
                                        })}
                                        keyValue='conventionalName'
                                        keyLabel='name'
                                        disablePortal={false}
                                        clearIcon={false}
                                    />
                                </Grid>
                            )}
                        </Grid>
                        <Grid container size={12} alignItems='center' columnSpacing={1}>
                            <Grid size='grow'>
                                <SimpleInput
                                    title={i18n.token}
                                    value={module.token}
                                    disabled
                                />
                            </Grid>
                            <Grid>
                                <ContentCopyRoundedIcon
                                    sx={{ marginTop: '0.7rem', cursor: 'pointer' }}
                                    onClick={() => {
                                        navigator.clipboard.writeText(module.token)
                                        dispatch(ToastrAction.success(i18n.copyToClipboardSucess))
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container alignItems='center' columnSpacing={1}>
                            <Grid size='grow'>
                                <ModuleUrl
                                    moduleParameters={moduleParameters}
                                    setModuleParameters={setModuleParameters}
                                    getGlobalModuleParameter={getGlobalModuleParameter}
                                    foundModuleParameter={foundModuleParameter}
                                    moduleLogin={module.login}
                                />
                            </Grid>
                            <Grid>
                                <Tooltip title={i18n.consult} placement='right' sx={{ marginTop: '8px' }}>
                                    <IconButton onClick={() => window.open(`https://${foundModuleParameter}/`, '_blank').focus()} disabled={!foundModuleParameter}>
                                        <LaunchRoundedIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    )
}

DefinitionPanel.propTypes = {
    module: PropTypes.shape({
        login: PropTypes.string,
        moduleTypeId: PropTypes.number,
    }),
    setModule: PropTypes.func,
}

export default DefinitionPanel